import { useState } from 'react'
import { ChevronDown, ChevronUp, Check, X, Sparkles } from 'lucide-react'
import type { AiReadiness } from '@/lib/api'

const CHECK_LABELS: Record<string, string> = {
  direct_answer: 'Respuesta directa (40-60 palabras)',
  pn_placement: 'Part number en título / meta / 1er párrafo',
  faqs: 'Preguntas frecuentes (4-6 reales)',
  specs_units: 'Especificaciones con unidades',
  compatibility: 'Compatibilidad (PN y modelos)',
  metas: 'Meta título / meta descripción',
  clean_copy: 'Sin relleno ni contradicciones',
  structure: 'Estructura semántica',
  structured_extras: 'Features / etiquetas / alt de imagen',
  multilang: 'Coherencia multi-idioma',
}

export function scoreColor(score: number) {
  if (score >= 80) return { text: 'text-success-700 dark:text-success-300', bg: 'bg-success-50 dark:bg-success-900/30', border: 'border-green-300 dark:border-green-700' }
  if (score >= 60) return { text: 'text-yellow-700 dark:text-yellow-300', bg: 'bg-yellow-50 dark:bg-yellow-900/30', border: 'border-yellow-300 dark:border-yellow-700' }
  return { text: 'text-danger-700 dark:text-red-300', bg: 'bg-danger-50 dark:bg-danger-900/30', border: 'border-red-300 dark:border-red-700' }
}

interface Props {
  readiness?: AiReadiness | null
  className?: string
  defaultOpen?: boolean
}

/** Badge "AI-ready X/100" con lista desplegable de checks y avisos. */
export default function AiReadinessBadge({ readiness, className = '', defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen)
  if (!readiness || typeof readiness.score !== 'number') return null
  const c = scoreColor(readiness.score)
  return (
    <div className={`rounded-lg border ${c.border} ${c.bg} ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium ${c.text}`}
      >
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          AI-ready {readiness.score}/100
          {readiness.warnings?.length > 0 && (
            <span className="text-xs font-normal opacity-80">· {readiness.warnings.length} aviso{readiness.warnings.length === 1 ? '' : 's'}</span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <ul className="space-y-1">
            {readiness.checks?.map(ch => (
              <li key={ch.key} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
                {ch.passed ? <Check className="w-3.5 h-3.5 text-success-600 mt-0.5 flex-shrink-0" /> : <X className="w-3.5 h-3.5 text-danger-500 mt-0.5 flex-shrink-0" />}
                <span className="flex-1">
                  <span className="font-medium">{CHECK_LABELS[ch.key] || ch.key}</span>
                  <span className="text-slate-500 dark:text-slate-400"> · {ch.points}/{ch.weight}</span>
                  {ch.detail && <span className="text-slate-500 dark:text-slate-400"> — {ch.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
          {readiness.warnings?.length > 0 && (
            <ul className="list-disc list-inside text-xs text-yellow-700 dark:text-yellow-300">
              {readiness.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
          {readiness.blocking?.length > 0 && (
            <p className="text-xs text-danger-600 dark:text-red-300 font-medium">Bloqueo: {readiness.blocking.join('; ')}</p>
          )}
        </div>
      )}
    </div>
  )
}
